import pulse from '@finn-no/pulse-sdk';
import Head from 'next/head';
import { memo, useEffect, useRef } from 'react';
import type { SspContext } from 'shared/types';
import { runAllMiddlewares } from '@/server/middleware';

export const scrollToTop = (): void => {
    window.scrollTo({
        top: 0,
        behavior: 'auto',
    });
};

const InjectScript = memo(({ script }: { script: string }) => {
    const divRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (divRef.current === null) {
            return;
        }

        // Creates a contextual fragment that will execute the script
        // NB! Only use this for scripts we have complete control over (i.e. no user input)
        //     as otherwise it would be a big security risk.
        const doc = document.createRange().createContextualFragment(script);

        // clear the div HTML, and append the doc fragment with the script
        divRef.current.innerHTML = '';
        divRef.current.appendChild(doc);
    });

    return <div ref={divRef} />;
});

const trackObjectPageView = () =>
    // Pulse tracker id: TRH_pv09 (see tracking section in README.md)
    pulse.trackPageView({
        object: {
            type: 'Page',
            category: 'travel > hotel',
            name: 'Kayak whitelabel',
        },
        vertical: {
            name: 'travel',
            subVertical: 'hotel',
        },
    });

const trackListingPageView = () =>
    // Pulse tracker id: TRH_pv08 (see tracking section in README.md)
    pulse.trackPageView({
        object: {
            type: 'Listing',
            category: 'travel > hotel',
            layout: 'List',
            name: 'Kayak whitelabel',
        },
        vertical: {
            name: 'travel',
            subVertical: 'hotel',
        },
    });

const changedPath = () => {
    if (window.location?.href?.includes('details')) {
        // Kayak cannot scroll to top for us, as they only control the iframe.
        // I.e. when a user has scrolled down the result list a bit and then clicks
        // to see the object page, only the content in the iframe changes. This means
        // the object page will still be scrolled down a bit. To fix this, we need
        // to scroll the page up on changes to the URL.
        // Only scroll to top for the hotel page.
        scrollToTop();

        trackObjectPageView();
    } else {
        trackListingPageView();
    }
};

const KayakWL = () => {
    useEffect(() => {
        window.addEventListener('hashchange', changedPath);

        return () => {
            window.removeEventListener('hashchange', changedPath);
        };
    }, []);

    const scriptToInject = `
        <script id="kayak-i-frame">
            (function(w,d,t,p,v,i,m,k,s){k = 'kayak-i-frame-loader';(w[k]=w[k]||[]).push(arguments);w[k].d=1*new Date();
            w[k].c=(w[k].d+'').substring(0,7);
            s=d.createElement(t),m=d.getElementsByTagName(t)[0];s.async=1;s.src='//'+p+'/kayak-i-frame-loader.js?_='+
            w[k].c;m.parentNode.insertBefore(s,m)}
            (window,document,'script','travel-kayak-wl.finn.no','hotels'));
        </script>
    `;

    return (
        <>
            <Head>
                <title>Hotell | FINN Reise</title>
            </Head>
            <main className="page-container">
                <InjectScript script={scriptToInject} />
            </main>
        </>
    );
};

export const getServerSideProps = async ({ req, res, query }: SspContext) => {
    await runAllMiddlewares(req, res, query);

    return { props: {} };
};

export default KayakWL;
